import {AvatarPicker, Button, Modal} from '@sollapay/ui/components';
import {useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {getInitials} from '@/utils';

import {AccountProfileFields, WelcomeAvatarHint} from './components';
import {useAccountSettingsForm} from '../hooks';

import type {UserProfileDTO} from '@sollapay/types';
import type {AvatarPickerHandle} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface WelcomeModalProps {
  isOpen: boolean;
  profile?: UserProfileDTO;
  avatarSeed?: string;
  isGuest?: boolean;
}

/** Non-dismissible first-login variant — it closes once a saved name flips `isNameConfirmed`. */
export const WelcomeModal: FC<WelcomeModalProps> = ({isOpen, profile, avatarSeed, isGuest}) => {
  const {t} = useTranslation('settings');
  const avatarPickerRef = useRef<AvatarPickerHandle>(null);
  const {form, handleSave, isSaving, avatarUrl, avatarError, onAvatarFileSelect, onRemoveAvatar} =
    useAccountSettingsForm({isOpen, profile, mode: 'welcome'});
  const [firstName, lastName] = form.watch(['firstName', 'lastName']);

  return (
    <Modal
      isOpen={isOpen}
      dismissible={false}
      headerAlign="center"
      headerVariant="hero"
      title={t('accountModal.welcome.title')}
      description={t(
        isGuest ? 'accountModal.welcome.guestDescription' : 'accountModal.welcome.description'
      )}
      illustration={
        <AvatarPicker
          ref={avatarPickerRef}
          src={avatarUrl}
          initials={getInitials(`${firstName} ${lastName}`)}
          seed={avatarSeed}
          isUploading={isSaving}
          onFileSelect={onAvatarFileSelect}
          uploadLabel={t('accountModal.avatar.upload')}
        />
      }
      headerAddon={
        <WelcomeAvatarHint
          hasAvatar={Boolean(avatarUrl)}
          error={avatarError}
          isDisabled={isSaving}
          onUpload={() => avatarPickerRef.current?.open()}
          onRemove={onRemoveAvatar}
        />
      }
      footer={
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          isLoading={isSaving}
          onClick={() => void handleSave()}
          iconPosition="end"
        >
          {t('accountModal.welcome.continue')}
        </Button>
      }
    >
      <AccountProfileFields form={form} email={profile?.email} />
    </Modal>
  );
};
